import type { ReactNode } from 'react';
import type { FieldRow } from '../../lib/endpointFields';
import { isRequirable } from '../../lib/requireFields';
import { m } from '../../paraglide/messages';

/**
 * A reference field card: rows grouped under their parent path
 * so a row reads `id` under "each comment" rather than `data.comments[].id`.
 */

interface FieldGroup {
  /** parent path the rows share, e.g. `data.comments[]`; empty at the root */
  path: string;
  /** the parent's own schema description, when it had one */
  description?: string;
  rows: { leaf: string; field: FieldRow }[];
}

const parentOf = (name: string): [string, string] => {
  const at = name.lastIndexOf('.');
  return at < 0 ? ['', name] : [name.slice(0, at), name.slice(at + 1)];
};

/** `comments` -> `comment`, `replies` -> `reply`; anything else unchanged. */
const singular = (word: string): string => {
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
};

const groupLabel = (path: string): string => {
  const leaf = parentOf(path)[1];
  return leaf.endsWith('[]') ? `each ${singular(leaf.slice(0, -2))}` : leaf;
};

/**
 * Rows bucketed by parent path. A container row whose members are listed is
 * dropped: the group label beneath it already names it.
 */
const groupFields = (rows: FieldRow[]): FieldGroup[] => {
  const names = rows.map((r) => r.name);
  const groups = new Map<string, FieldGroup>();
  const containers = new Map<string, string | undefined>();
  for (const field of rows) {
    const hasMembers = names.some(
      (n) => n.startsWith(`${field.name}.`) || n.startsWith(`${field.name}[]`),
    );
    if (hasMembers) {
      containers.set(field.name, field.description);
      containers.set(`${field.name}[]`, field.description);
      continue;
    }
    const [path, leaf] = parentOf(field.name);
    const group = groups.get(path) ?? { path, description: containers.get(path), rows: [] };
    group.rows.push({ leaf, field });
    groups.set(path, group);
  }
  return [...groups.values()];
};

/** A JSON example as a reader sees it: strings without their quotes. */
const plainValue = (json: string): string => {
  try {
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'string' ? parsed : json;
  } catch {
    return json;
  }
};

const Row = ({
  leaf,
  field,
  shaded,
  requirable,
}: {
  leaf: string;
  field: FieldRow;
  shaded: boolean;
  requirable: Set<string>;
}) => (
  <li className={`rounded-8 px-3 py-2 ${shaded ? 'bg-surface-2' : ''}`}>
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="mono break-all text-13 text-text">{leaf}</span>
      <span className="mono rounded-4 bg-line-2 px-1.5 py-px text-11 text-text-3">
        {field.type}
      </span>
      {isRequirable(field.name, requirable) ? (
        <span className="text-11 text-accent">{m.endpoint_spec_can_require()}</span>
      ) : null}
    </div>
    {field.value ? (
      <p className="mt-0.5 break-words text-13 leading-snug text-text-2">
        {plainValue(field.value)}
      </p>
    ) : null}
    {field.description ? (
      <p className="mt-0.5 text-12 leading-snug text-text-3">{field.description}</p>
    ) : null}
  </li>
);

export interface FieldCardProps {
  title: string;
  caption: string;
  /** a logo at the left of the header */
  mark?: ReactNode;
  rows: FieldRow[];
  requirable?: Set<string>;
  /** controls for the right of the header */
  actions?: ReactNode;
  /** shown in place of the field list, e.g. the TypeScript interface */
  replacement?: ReactNode;
  /** below the list, e.g. a way to report a missing field */
  footer?: ReactNode;
}

/** A titled card listing fields grouped under their parent path. */
export const FieldCard = ({
  title,
  caption,
  mark,
  rows,
  requirable = new Set<string>(),
  actions,
  replacement,
  footer,
}: FieldCardProps) => (
  <section className="min-w-0 rounded-14 border border-rule bg-surface p-4 s520:p-5">
    <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 px-3">
      <div className="flex items-center gap-3">
        {mark}
        <div>
          <h3 className="text-15 font-medium text-text">{title}</h3>
          <p className="mt-0.5 text-12 text-text-3">{caption}</p>
        </div>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
    {replacement ?? (rows.length === 0 ? (
      <p className="mt-4 px-3 text-13 text-text-3">{m.endpoint_fields_none()}</p>
    ) : (
      groupFields(rows).map((group, index) => (
        <div key={group.path || 'root'} className="mt-4">
          {/* The root needs a label only once another group sits above it. */}
          {group.path || index > 0 ? (
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 px-3">
              <span className="text-12 font-medium text-text-2">
                {group.path ? groupLabel(group.path) : 'top level'}
              </span>
              <span className="mono break-all text-11 text-text-3">{group.path}</span>
            </div>
          ) : null}
          {group.description ? (
            <p className="mb-1 px-3 text-12 leading-snug text-text-3">{group.description}</p>
          ) : null}
          <ul className="flex flex-col">
            {group.rows.map(({ leaf, field }, i) => (
              <Row
                key={field.name}
                leaf={leaf}
                field={field}
                shaded={i % 2 === 1}
                requirable={requirable}
              />
            ))}
          </ul>
        </div>
      ))
    ))}
    {footer ? <div className="mt-4">{footer}</div> : null}
  </section>
);
